<template>
  <section>
    <Load v-if="isLoading" />

    <template v-if="order.data">
      <OrderDetailStudio7 @fetch="refetch" />
    </template>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { ORDER_STATUS } from '@/config/order'

export default {
  name: 'Success',

  computed: {
    ...mapState({
      order: state => state.order,
      isLoading: state => state.order.isLoading,
      isPayAtStoreExpired: state => state.order.isExpired,
    }),

    ...mapGetters({
      status: 'order/orderStatus',
    }),
  },

  data() {
    return {
      timer: null,
      ORDER_STATUS,
      hasSentPurchase: false, // กันยิง event purchase ซ้ำ
    }
  },

  components: {
    Load: () => import('@/views/Load'),
    OrderDetailStudio7: () =>
      import('@/components/studio7/OrderDetailStudio7'),
  },

  watch: {
    isPayAtStoreExpired(val, oldVal) {
      if (val && val !== oldVal) {
        this.timer = setInterval(this.refetch, 1800000)
      }
    },
    status(val, oldVal) {
      this.setLatestSlug()
      this.trySendPurchase()

      const {
        CREATE,
        PROCESS,
        PRE_COMPLETE,
        COMPLETE,
      } = this.ORDER_STATUS

      if (
        this.status &&
        ![CREATE, PROCESS, PRE_COMPLETE].includes(this.status)
      ) {
        this.cancelAutoUpdate()
        this.$store.dispatch(
          'order/unsubscribeOrderStatus',
          this.$route.params.uid,
        )

        if (val && oldVal && val !== oldVal && val === COMPLETE) {
          this.refetch()
        }
      } else {
        if (val && oldVal && val !== oldVal) {
          this.refetch()
        } else {
          this.$store.dispatch(
            'order/subscribeOrderStatus',
            this.$route.params.uid,
          )
        }
      }
    },
  },

  mounted() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
    this.sentEventGoogleTAG()
    this.trySendPurchase()
  },

  created() {
    this.refetch()
    this.timer = setInterval(this.refetch, 1800000) // 30 minutes
  },

  beforeUnmount() {
    this.cancelAutoUpdate()
  },

  beforeDestroy() {
    this.cancelAutoUpdate()
  },

  methods: {
    setLatestSlug() {
      if (this.order?.data?.campaign?.slug) {
        localStorage.setItem(
          'productLatestVisit',
          this.order.data.campaign.slug,
        )
      }
    },

    async refetch() {
      const uid = this.$route.params.uid
      if (uid) {
        try {
          await this.$store.dispatch('order/getOrder', { uuid: uid })
          this.$store.dispatch(
            'order/subscribeOrderStatus',
            this.$route.params.uid,
          )
        } catch {
          this.cancelAutoUpdate()
        }
      }
      this.$forceUpdate()
    },

    cancelAutoUpdate() {
      clearInterval(this.timer)
    },

    sentEventGoogleTAG() {
      try {
        window.dataLayer.push({
          event: 'Thank You',
          page: 'thank',
          data: {
            full_name: this.order.data.full_name,
            sku: this.order.data.sku,
            status: this.order.data.status,
            total_deposit: this.order.data.total_deposit,
            total_price: this.order.data.total_price,
            bundle_products: this.order.data.bundle_products,
          },
        })
      } catch (e) {
        return
      }
    },

    // ยิง event purchase ครั้งเดียว เมื่อออเดอร์ชำระเงินสำเร็จ (COMPLETE / SUCCESS)
    trySendPurchase() {
      const { COMPLETE, SUCCESS } = this.ORDER_STATUS

      if (
        this.hasSentPurchase ||
        this.order?.data?.length === 0 ||
        ![COMPLETE, SUCCESS].includes(this.status)
      ) {
        return
      }

      this.hasSentPurchase = true
      this.sentPurchaseEvent()
    },

    // สร้าง payload รูปแบบ GA4 ecommerce แล้ว push เข้า dataLayer
    sentPurchaseEvent() {
      try {
        const data = this.order.data

        // รวมรายการสินค้า: สินค้าหลัก + bundle ทั้งหมด
        const items = []

        if (data.product_main?.sku) {
          items.push({
            item_id: data.product_main.sku,
            item_name: data.product_main.name,
            price: Number(data.product_main.price) || 0,
            quantity: 1,
          })
        }

        ;(data.bundle_products || []).forEach((bundle, index) => {
          items.push({
            item_id: bundle.sku,
            item_name: bundle.name,
            item_category: bundle.type,
            price: Number(bundle.price_srp) || 0,
            quantity: 1,
            index: index + 1,
          })
        })

        // ค่าจัดส่ง: ดึงจาก bundle ที่เป็น delivery ถ้ามี
        const shippingBundle = (data.bundle_products || []).find(
          b => b.type === 'delivery',
        )
        const shipping = shippingBundle
          ? Number(
              shippingBundle.price_ship ?? shippingBundle.price_srp,
            ) || 0
          : 0

        // Discount: ดึงจาก order.discount + order.discount_coupon
        const discount =
          Number(data.discount) + Number(data.discount_coupon) || 0

        const payment = {
          payment_type: data.payment_type ?? '',
          deposit: data.deposit ?? 0,
          remaining: data.remaining ?? 0,
        }

        window.dataLayer = window.dataLayer || []
        // เคลียร์ ecommerce เดิมก่อน กันข้อมูลจาก event ก่อนหน้าปนกัน (แนวทาง GA4)
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: data.order,
            value: Number(data.total_price) || 0,
            currency: 'THB',
            coupon: data.coupon_code ?? '',
            coupon_id: data.coupon_used ?? '', // coupon_id จากระบบ backend
            payment,
            shipping,
            discount,
            items,
          },
        })
      } catch (e) {
        return
      }
    },
  },
}
</script>
